import { Box, Container, Hidden, Typography } from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import Imgix from "react-imgix";
import { useInView } from "react-intersection-observer";
import FadeInUp from "../../components/animations/FadeInUp";
import ModalCloseButton from "../../components/basic-modal/ModalCloseButton";
import { convertDatoCmsAssetUrlToCloudfrontUrl } from "../../helpers/cdn";
import { formatMultilinesText } from "../../helpers/markdown";
import useTranslate from "../../hooks/use-translate";
import BarChart from "./BarChart";
import {
  CustomBackdropOverlapsHeader,
  CustomHeading,
  CustomModal,
  CustomModalTransition,
  CustomVideo,
  LearnMoreButton
} from "./styles";

export default function DeviceFeaturesBarChartsSection({
  text,
  secondaryText,
  desktopImage,
  mobileImage,
  showBarChart,
  barChart,
  videoSources,
  videoMode,
  learnMore
}: Queries.DeviceFeaturesBarChartsFragmentFragment) {
  const t = useTranslate();
  const [isLearnMoreModalOpen, setLearnMoreModalOpen] = useState(false);

  const barChartData = barChart
    ? JSON.parse(barChart as unknown as string)
    : null;

  const hasConfiguredVideos = videoSources?.length > 0;
  const videoPoster = videoSources?.find(v => v.mimeType === "video/mp4")?.video
    .thumbnailUrl; // Use mp4 video has source for poster, else it will have a black background

  const videoRef = useRef<HTMLVideoElement>();
  const [inViewRef, inView] = useInView({
    threshold: 0.5,
    skip: videoMode === "loop" || !hasConfiguredVideos
  });

  /**
   * Default video for non-looping videos:
   * 1. Play video when entering the viewport
   * 2. Reset video after leaving the viewport
   * 3. Play video again when re-entering the viewport
   */
  useEffect(() => {
    if (!hasConfiguredVideos || !videoRef.current || videoMode === "loop") {
      return;
    }
    if (inView) {
      // Older browsers may not return a value from play()
      const promise = videoRef.current.play();
      if (promise) {
        promise.catch(e => {});
      }
    } else {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (error) {}
    }
  }, [inView]);

  return (
    <Container maxWidth="md">
      <Box display="flex" flexDirection={{ xs: "row-reverse", sm: "row" }}>
        {/* Image/Video */}
        <Box
          width={{ xs: "148px", sm: "270px" }}
          marginRight={{ xs: 0, sm: 6, md: 10 }}
          marginLeft={{ xs: 2, sm: 0 }}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          ref={inViewRef}
        >
          {/* Device video */}
          {hasConfiguredVideos && (
            <CustomVideo
              controls={false}
              preload="auto"
              loop={videoMode === "loop" || text === "Dosage control."}
              autoPlay={videoMode === "loop"}
              muted
              playsInline
              // poster={`${videoPoster}?time=0`}
              ref={videoRef}
              disableRemotePlayback={true}
            >
              {videoSources.map(videoSource => (
                <source
                  key={videoSource.url}
                  src={convertDatoCmsAssetUrlToCloudfrontUrl(videoSource.url)}
                  type={videoSource.mimeType}
                />
              ))}
            </CustomVideo>
          )}

          {/* Fallback to static image */}
          {!hasConfiguredVideos && (
            <>
              {/* Mobile */}
              <Hidden mdUp implementation="css">
                <Imgix
                  src={mobileImage.url}
                  width={148}
                  imgixParams={{
                    q: 75,
                    auto: "format,compress"
                  }}
                  htmlAttributes={{
                    loading: "lazy"
                  }}
                />
              </Hidden>
              {/* Desktop */}
              <Hidden smDown implementation="css">
                <Imgix
                  src={desktopImage.url}
                  width={320}
                  imgixParams={{
                    q: 75,
                    auto: "format,compress"
                  }}
                  htmlAttributes={{
                    loading: "lazy"
                  }}
                />
              </Hidden>
            </>
          )}
        </Box>

        {/* Text + Bar chart */}
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          justifyContent="center"
          marginLeft={{ xs: 0, sm: 0 }}
        >
          <FadeInUp>
            <CustomHeading
              variant="h4"
              dangerouslySetInnerHTML={{ __html: formatMultilinesText(text) }}
              className="white"
            />
          </FadeInUp>

          {secondaryText && (
            <FadeInUp delay={0.5}>
              <Box mt={{ xs: 3, md: 4 }}>
                <Typography
                  variant="h6"
                  className="white"
                  dangerouslySetInnerHTML={{
                    __html: formatMultilinesText(secondaryText)
                  }}
                />
              </Box>
            </FadeInUp>
          )}

          {/* Learn more */}
          {learnMore && (
            <FadeInUp delay={0.5}>
              <LearnMoreButton onClick={() => setLearnMoreModalOpen(true)}>
                <span className="plus-icon">
                  <svg
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.80005 1.49463V13.0946V1.49463Z"
                      fill="#F3F0E9"
                    />
                    <path
                      d="M6.80005 1.49463V13.0946"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path d="M12.6 7.29468H1H12.6Z" fill="#F3F0E9" />
                    <path
                      d="M12.6 7.29468H1"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>

                <span>{t("learnMore")}</span>
              </LearnMoreButton>
            </FadeInUp>
          )}
        </Box>
      </Box>

      <CustomModal
        open={isLearnMoreModalOpen}
        onClose={() => setLearnMoreModalOpen(false)}
        closeAfterTransition
        disableAutoFocus
        BackdropComponent={CustomBackdropOverlapsHeader}
        BackdropProps={{
          timeout: {
            enter: 250,
            exit: 250
          }
        }}
        // style={zIndex ? { zIndex } : null}
        overlapsHeader
      >
        {/* Animated modal */}
        <CustomModalTransition in={isLearnMoreModalOpen}>
          <Box
            paddingX={{ xs: 3, md: 6 }}
            paddingBottom={{ xs: 3, md: 8 }}
            paddingTop={{ xs: 3, md: 6 }}
          >
            <Box mb={{ xs: 2, md: 2 }}>
              <Typography
                variant="h4"
                dangerouslySetInnerHTML={{ __html: formatMultilinesText(text) }}
              />
            </Box>
            <Typography
              variant="h6"
              dangerouslySetInnerHTML={{
                __html: formatMultilinesText(learnMore)
              }}
            />

            {/* Bar Chart */}
            {showBarChart && barChartData && (
              <Box mt={6}>
                <BarChart data={barChartData} />
              </Box>
            )}
          </Box>

          {/* Close button */}
          <ModalCloseButton
            variant="dark"
            onClick={() => setLearnMoreModalOpen(false)}
            style={{ zIndex: 200 }}
          />
        </CustomModalTransition>
      </CustomModal>
    </Container>
  );
}

export const DeviceFeaturesBarChartFragment = graphql`
  fragment DeviceFeaturesBarChartsFragment on DatoCmsCModuleDeviceFeaturesSection {
    text
    secondaryText
    desktopImage: asset {
      url
    }
    mobileImage: assetMobile {
      url
    }
    videoSources {
      mimeType
      url
      video {
        thumbnailUrl(format: png)
      }
    }
    videoMode
    showBarChart
    barChart
    learnMore
    container
    containerDisableGutters
    containerBackgroundColor
    visibilitySettings {
      ...ContentModuleVisibilitySettingFragment
    }
    model {
      apiKey
    }
  }
`;
